import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ExtendedAuthContextType } from './authContextTypes';
import { User, UserRole } from './types';
import { supabaseAuthService } from './supabaseAuthService';
import { checkAndRegisterDevice } from '@/services/deviceFingerprint';
import { notifyNewDeviceLogin } from '@/services/deviceNotifications';
import { isNativeMobile } from '@/utils/platformDetection';
import { medianBiometricAuth } from '@/services/medianBiometricAuth';
import { logger } from '@/services/logger';
import medianNativeBridge, { logoutOneSignal, loginOneSignal, grantOneSignalPrivacyConsent, pushAuthToNative, clearNativeAuth, getAuthFromNative } from '@/utils/medianNativeBridge';
import { pushTokenService } from '@/services/pushTokenService';
import { useSaveIndicatorStore } from '@/stores/saveIndicatorStore';
import { persistentAuthStorage } from '@/services/auth/persistentAuthStorage';
import { appTransitionState } from '@/services/appTransitionState';
import { crashMarkerService } from '@/services/crashMarkerService';
import { useRealtimeAccountStatus } from '@/hooks/useRealtimeAccountStatus';
import { eventBus } from '@/lib/eventBus';
import { clearCarpoolMatchingCache } from '@/hooks/useCarpoolMatching';
import { withCaptcha } from '@/lib/captcha';

// ✅ Constante pour le debounce (augmenté pour mobile)
const FETCH_DEBOUNCE_MS = 3000; // 3s minimum entre appels fetchUserProfile

export const useAuthLogic = (): ExtendedAuthContextType => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // ✅ FIX RACE CONDITION: Flag pour indiquer que l'auth est stable (enrichi ou pas de session)
  const [isAuthStable, setIsAuthStable] = useState(false);
  const queryClient = useQueryClient();

  // Refs pour le debounce et éviter les appels redondants
  const lastFetchTimeRef = useRef<number>(0);
  const lastProcessedEventRef = useRef<string>('');
  const fetchInProgressRef = useRef<boolean>(false);
  
  // ✅ FIX LOGOUT CRASH: Flag pour ignorer les effets pendant le logout
  const logoutInProgressRef = useRef<boolean>(false);

  // 🔐 Garde-fou anti-boucle pour la récupération de session depuis le Keychain
  // natif sur SIGNED_OUT involontaire (token roté en background → localStorage
  // périmé). Réarmé après une récupération réussie ET après un SIGNED_IN
  // manuel (sinon un seul échec transitoire désactivait la récupération pour
  // toute la vie de la page). Voir le handler SIGNED_OUT.
  const nativeRecoveryAttemptedRef = useRef<boolean>(false);

  // 🔐 SignOut applicatif FORCÉ (compte désactivé, session invalide confirmée,
  // refresh fatal après échec de récupération) : posé juste avant le
  // supabase.auth.signOut() de ces chemins pour que le handler SIGNED_OUT
  // purge immédiatement (départ réel) au lieu de tenter une récupération —
  // récupérer la session d'un compte désactivé rejouerait la boucle
  // désactivation → signOut.
  const forcedSignOutRef = useRef<boolean>(false);

  // ✅ Realtime: déconnexion instantanée si le compte est suspendu
  useRealtimeAccountStatus(user?.id);

  // ✅ FIX SESSION:INVALID - Listener global pour les sessions expirées/invalides
  // Garantit qu'un utilisateur coincé dans un état zombie (session expirée milieu d'opération RGPD) 
  // est redirigé vers /login même si aucun autre handler n'est actif
  useEffect(() => {
    const handleSessionInvalid = async () => {
      logger.warn('[AUTH] 🔒 session:invalid event received, verifying before forcing sign out');
      if (logoutInProgressRef.current) return;

      // ⚠️ Vérifier que la session est RÉELLEMENT invalide avant de déconnecter.
      // Cause historique : une simple erreur RLS transitoire émettait
      // session:invalid → signOut → boucle de reconnexion (3 fois d'affilée
      // pour l'utilisateur, bug 2026-05-31).
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          // Tenter un refresh pour confirmer
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError) {
            logger.debug('[AUTH] session:invalid was a false positive: session is healthy, ignoring');
            return;
          }
          logger.warn('[AUTH] session:invalid confirmed by refresh failure:', refreshError.message);
        }
      } catch (e) {
        logger.warn('[AUTH] session:invalid verification threw, treating as invalid:', e);
      }

      forcedSignOutRef.current = true;
      await supabase.auth.signOut();
      window.location.href = '/login?reason=session_expired';
    };

    const cleanup = eventBus.on('session:invalid', handleSessionInvalid);
    return cleanup;
  }, []);


  /**
   * Build minimal user from session JWT claims (ultime fallback)
   */
  const buildMinimalUserFromSession = (session: any): User => {
    const appMetadata = session.user.app_metadata || {};
    const userMetadata = session.user.user_metadata || {};

    return {
      id: session.user.id,
      email: session.user.email || '',
      role: (appMetadata.primary_role as UserRole) || 'employee',
      nickname: userMetadata.nickname || session.user.email?.split('@')[0] || 'User',
      name: userMetadata.full_name || session.user.email?.split('@')[0] || 'User',
      organizationId: appMetadata.organization_id || userMetadata.organizationId || undefined,
      status: 'pending',
      userType: 'client',
      // Champs non disponibles en mode dégradé
      departmentId: undefined,
      teamId: undefined,
      managerId: undefined,
      department: undefined,
      team: undefined,
      company: undefined,
    };
  };

  useEffect(() => {
    logger.debug('Auth context initializing...');
    let isInitialized = false;
    let isMounted = true;

    // 🔐 Récupération de session via le Keychain natif — partagée entre le
    // handler SIGNED_OUT, le filet de cold boot (getSession) et les chemins
    // de refresh fatal (proactif / visibility). Une seule tentative par
    // cycle (nativeRecoveryAttemptedRef), timeout 3 s sur le bridge natif.
    // Renvoie true si la session est rétablie (refreshSession a émis
    // TOKEN_REFRESHED → pushAuthToNative re-synchronise le Keychain).
    const tryNativeSessionRecovery = async (): Promise<boolean> => {
      if (nativeRecoveryAttemptedRef.current) return false;
      nativeRecoveryAttemptedRef.current = true;
      try {
        const native = await Promise.race([
          getAuthFromNative(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
        ]);
        if (!native?.refreshToken) return false;
        const { data: recData, error: recError } = await supabase.auth.refreshSession({
          refresh_token: native.refreshToken,
        });
        if (recError || !recData.session) {
          logger.warn('[AUTH LOGIC] 🔐 Récupération Keychain échouée');
          return false;
        }
        logger.debug('[AUTH LOGIC] 🔐 Session récupérée depuis le Keychain natif (token roté en background)');
        nativeRecoveryAttemptedRef.current = false;
        return true;
      } catch {
        return false;
      }
    };

    // ✅ FIX LOGOUT CRASH: onAuthStateChange DOIT être 100% SYNCHRONE
    // Toutes les opérations async sont déférées via setTimeout(0)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('Auth state changed:', { event, user: session?.user?.email });

        // ✅ FIX: Si logout en cours, ignorer TOUT sauf le setUser(null)
        if (logoutInProgressRef.current) {
          logger.debug('[AUTH] Logout in progress, ignoring enrichment');
          if (event === 'SIGNED_OUT') {
            // Départ VOLONTAIRE : c'est ICI que son SIGNED_OUT arrive (le
            // handler principal plus bas n'est jamais atteint avec le ref
            // posé — revue 03/08 : la purge ne partait donc jamais au
            // logout volontaire). Nettoyage credentials natifs + file de
            // tickets feedback (contexte technique en clair sur appareil
            // partagé).
            void clearNativeAuth();
            void import('@/services/bugReportService')
              .then((m) => m.clearPendingFeedbackReports())
              .catch(() => {});
          }
          if (!session?.user && isMounted) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        // Ignorer INITIAL_SESSION si SIGNED_IN déjà traité
        const eventKey = `${event}-${session?.user?.id || 'none'}`;
        if (event === 'INITIAL_SESSION' && lastProcessedEventRef.current.startsWith('SIGNED_IN')) {
          logger.debug('Skipping INITIAL_SESSION (SIGNED_IN already processed)');
          return;
        }

        // Debounce pour éviter les appels rapides successifs
        const now = Date.now();
        if (now - lastFetchTimeRef.current < FETCH_DEBOUNCE_MS && fetchInProgressRef.current) {
          logger.debug('Skipping duplicate fetchUserProfile (debounce)');
          return;
        }

        lastProcessedEventRef.current = eventKey;

        // 🔐 Compteur "logins réussis" pour gating du prompt biométrique (best practice Apple HIG / Google Identity)
        if (event === 'SIGNED_IN' && session?.user) {
          // Un login manuel réussi clôt le cycle de récupération précédent :
          // on réarme le garde-fou (sinon un unique échec transitoire
          // désactivait la récupération Keychain à vie de la page).
          nativeRecoveryAttemptedRef.current = false;
          try {
            // import dynamique pour éviter cycle
            import('@/services/biometricPromptPolicy').then(m => m.biometricPromptPolicy.recordSuccessfulLogin());
          } catch { /* ignore */ }
        }

        if (session?.user) {
          logger.debug('[AUTH LOGIC] User session found', { email: session.user.email });

          // ⚡ INSTANT AUTH : Définir immédiatement un user minimal (SYNCHRONE)
          const minimalUser = buildMinimalUserFromSession(session);

          if (isMounted) {
            setUser(minimalUser);
            setIsLoading(false);
            logger.debug('[AUTH LOGIC] Minimal user set IMMEDIATELY', { email: minimalUser.email, role: minimalUser.role });
          }

          // 📲 Bg-sync natif → serveur (Option A) : pousser le refresh_token
          // Supabase au plugin natif (Keychain) sur SIGNED_IN et TOKEN_REFRESHED,
          // pour que le background observer iOS puisse auto-valider les objectifs
          // côté serveur même app fermée. Fire-and-forget, tolérant (no-op si pas
          // de bridge natif / méthode absente) — ne jamais bloquer le flux auth
          // synchrone. Le token rafraîchi est repoussé pour rester en phase avec
          // la rotation côté natif.
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            void pushAuthToNative(session);
          }

          if (!isInitialized) {
            isInitialized = true;
            logger.debug('[AUTH LOGIC] Auth initialized via onAuthStateChange (instant)');
          }

          // ✅ FIX: Toutes les opérations async DÉFÉRÉES via setTimeout(0)
          // Cela évite les deadlocks avec Supabase auth
          setTimeout(() => {
            // Vérifier à nouveau si logout en cours après le defer
            if (logoutInProgressRef.current || !isMounted) return;




              // ✅ AUTO-REPAIR: Single orchestrated push state check & repair
              // Replaces 4 separate parallel calls with one coordinated flow
              if (medianNativeBridge.isMedianNative() && session?.user) {
                logger.debug('[AUTH LOGIC] 📱 Session detected - checking RGPD consent before OneSignal...');
                
                // ✅ RGPD GUARD: Vérifier le consentement data_processing AVANT tout appel OneSignal
                (async () => {
                  try {
                    const { data: consent } = await supabase
                      .from('consent_logs')
                      .select('consent_given')
                      .eq('user_id', session.user.id)
                      .eq('consent_type', 'data_processing')
                      .eq('consent_given', true)
                      .is('revoked_at', null)
                      // Plusieurs consentements actifs (renouvellement) : sans
                      // limite, maybeSingle() rend null et rien ne s'inscrit.
                      .order('created_at', { ascending: false })
                      .limit(1)
                      .maybeSingle();
                    
                    if (logoutInProgressRef.current || !isMounted) return;
                    
                    if (!consent) {
                      logger.warn('[AUTH LOGIC] ⚠️ No RGPD consent, skipping OneSignal registration');
                      return;
                    }
                    
                    logger.debug('[AUTH LOGIC] ✅ RGPD consent confirmed, proceeding with OneSignal');
                    
                    // ✅ Grant privacy consent to Median bridge (required for Manual mode)
                    grantOneSignalPrivacyConsent();
                    
                    // 🔑 STEP 1: loginOneSignal + registerPushNotifications
                    loginOneSignal(session.user.id);
                    medianNativeBridge.registerPushNotifications();
                    logger.debug('[AUTH LOGIC] ✅ OneSignal login + register called');
                    
                    // 🔧 STEP 2: autoRepairPushState orchestrates all checks sequentially
                    pushTokenService.autoRepairPushState(session.user.id)
                      .then(repairResult => {
                        if (logoutInProgressRef.current || !isMounted) return;
                        logger.info('[AUTH LOGIC] 🔧 autoRepairPushState result:', repairResult);
                      })
                      .catch(err => {
                        logger.warn('[AUTH LOGIC] autoRepairPushState failed (non-blocking):', err);
                      });
                    
                    // Cleanup invalid tokens (silent, independent)
                    supabase.functions.invoke('cleanup-invalid-push-tokens', {
                      body: { userId: session.user.id }
                    }).catch(() => {});
                  } catch (err) {
                    logger.warn('[AUTH LOGIC] RGPD consent check failed (non-blocking):', err);
                  }
                })();
              }

            // ENRICHISSEMENT EN ARRIÈRE-PLAN (non bloquant)
            if (fetchInProgressRef.current || Date.now() - lastFetchTimeRef.current < FETCH_DEBOUNCE_MS) {
              logger.debug('[AUTH LOGIC] Skipping background enrichment (debounce or in progress)');
              setIsAuthStable(true); // ✅ FIX: Marquer stable même si enrichissement sauté
              return;
            }

            fetchInProgressRef.current = true;
            lastFetchTimeRef.current = Date.now();

            supabaseAuthService.fetchUserProfile(session.user.id)
              .then((enrichedUser) => {
                fetchInProgressRef.current = false;

                // Vérifier que logout n'est pas en cours avant de mettre à jour
                if (logoutInProgressRef.current || !isMounted) return;

                if (enrichedUser) {
                  // Bloquer les utilisateurs désactivés
                  if (enrichedUser.status === 'rejected') {
                    logger.warn('[AUTH LOGIC] User account is DISABLED:', { email: enrichedUser.email });
                    forcedSignOutRef.current = true;
                    supabase.auth.signOut().then(() => {
                      if (isMounted && !logoutInProgressRef.current) {
                        setUser(null);
                        setIsLoading(false);
                        window.location.href = '/login?reason=account_disabled';
                      }
                    });
                    return;
                  }

                  logger.debug('[AUTH LOGIC] Background enrichment SUCCESS', { email: enrichedUser.email });
                  setUser(enrichedUser);
                  setIsAuthStable(true); // ✅ Marquer auth comme stable après enrichissement
                }
              })
              .catch((error: any) => {
                fetchInProgressRef.current = false;
                setIsAuthStable(true); // ✅ FIX: Marquer stable même si enrichissement échoue
                logger.warn('[AUTH LOGIC] Background enrichment failed (keeping minimal user):', { error: error?.message });
              });
          }, 0);

        } else {
          logger.debug('[AUTH LOGIC] No session found, user set to null');
          if (isMounted) {
            setUser(null);
            setIsLoading(false);
            setIsAuthStable(true); // ✅ Marquer auth comme stable (pas de session)
          }

          // 📲 Bg-sync natif → serveur (Option A) : au SIGNED_OUT, effacer les
          // credentials Supabase stockés côté natif (Keychain) pour ne plus
          // server-sync au nom d'un utilisateur déconnecté. Le background
          // observer retombe alors sur la notif locale seule. Fire-and-forget,
          // tolérant. Gate sur SIGNED_OUT uniquement (pas sur un INITIAL_SESSION
          // sans session au cold boot, où il n'y a rien à effacer).
          if (event === 'SIGNED_OUT') {
            // 🎟️ + 🔐 Départ RÉEL uniquement : purge de la file de tickets
            // feedback (contexte technique en clair sur appareil partagé, RLS
            // bloquerait de toute façon l'insertion sous une autre session) et
            // nettoyage des credentials natifs. À NE PAS exécuter quand la
            // session du même utilisateur est récupérée juste après.
            const finalizeRealSignOut = () => {
              void clearNativeAuth();
              void import('@/services/bugReportService')
                .then((m) => m.clearPendingFeedbackReports())
                .catch(() => {});
            };

            if (forcedSignOutRef.current) {
              // SignOut applicatif FORCÉ (compte désactivé, session invalide
              // confirmée, refresh fatal après récupération déjà tentée) :
              // départ réel immédiat, pas de récupération.
              forcedSignOutRef.current = false;
              finalizeRealSignOut();
            } else {
              // Déconnexion INVOLONTAIRE. Cause iOS vérifiée : le background
              // observer (KokenHealthKitBackground.swift) ROTE le refresh_token
              // app fermée et réécrit le nouveau dans le Keychain, jamais dans
              // le localStorage du WebView → au réveil le token du localStorage
              // est périmé → supabase _removeSession() → SIGNED_OUT.
              //
              // On tente UNE fois de récupérer le token VIVANT depuis le
              // Keychain et de rétablir la session. Sans régression : on agit
              // APRÈS que la session locale a déjà été supprimée par supabase,
              // il n'y a donc aucune session valide à écraser (le piège
              // _callRefreshToken → _removeSession sur token mort est déjà
              // passé). Différé via setTimeout(0) pour éviter tout deadlock
              // avec le lock interne de onAuthStateChange. Sur un build iOS
              // sans getAuth (shell antérieur), getAuthFromNative renvoie null
              // → comportement identique à avant (déconnexion réelle).
              setTimeout(() => {
                void (async () => {
                  try {
                    if (logoutInProgressRef.current || forcedSignOutRef.current) {
                      forcedSignOutRef.current = false;
                      finalizeRealSignOut();
                      return;
                    }
                    const recovered = await tryNativeSessionRecovery();
                    if (!recovered) finalizeRealSignOut();
                  } catch {
                    // Jamais laisser un état intermédiaire : échec inattendu
                    // (bridge natif muet, exception non-AuthError) = départ réel.
                    finalizeRealSignOut();
                  }
                })();
              }, 0);
            }
          }

          if (!isInitialized) {
            isInitialized = true;
            logger.debug('[AUTH LOGIC] Auth initialized (no session)');
          }
        }
      }
    );

    // 2. Check for existing session and detect invalid sessions
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      // Détecter les erreurs de session invalide - tentative de refresh d'abord
      if (error) {
        logger.error('[AUTH] Session error:', { message: error.message });
        if (error.message?.includes('session') || error.message?.includes('Session not found') || error.status === 403) {
          logger.warn('[AUTH] Session invalide détectée, tentative de refresh...');

          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

          if (refreshError || !refreshData.session) {
            // Dernier recours avant la déconnexion forcée : le Keychain natif
            // détient peut-être le token roté en background.
            if (await tryNativeSessionRecovery()) {
              logger.debug('[AUTH] Session restaurée via le Keychain natif (boot)');
              return;
            }
            logger.warn('[AUTH] Refresh échoué, déconnexion forcée');
            forcedSignOutRef.current = true;
            await supabase.auth.signOut();
            setUser(null);
            setIsLoading(false);
            window.location.href = '/login?reason=session_expired';
            return;
          } else {
            logger.debug('[AUTH] Session restaurée avec succès via refresh');
            return;
          }
        }
      }

      // Set loading false si pas déjà initialisé et pas de session
      if (!session && !isInitialized && isMounted) {
        // Filet cold boot : le SIGNED_OUT émis par _recoverAndRefresh peut
        // précéder l'abonnement onAuthStateChange (course au boot) — la
        // récupération du handler ne se déclenche alors jamais. Même garde
        // une-fois ; no-op immédiat hors iOS natif (getAuth absent → null).
        const recovered = await tryNativeSessionRecovery();
        if (!recovered && isMounted) {
          isInitialized = true;
          setIsLoading(false);
          logger.debug('[AUTH LOGIC] No session found via getSession');
        }
      }
    });

    // ✅ Mutex partagé pour empêcher refresh concurrents (évite token révoqué par race)
    let refreshInProgress = false;
    let lastSuccessfulRefresh = 0;

    const isFatalRefreshError = (msg?: string): boolean => {
      if (!msg) return false;
      const m = msg.toLowerCase();
      return m.includes('refresh_token_not_found')
        || m.includes('invalid refresh token')
        || m.includes('refresh token not found')
        || m.includes('already used');
    };

    const safeRefreshSession = async (origin: string): Promise<{ ok: boolean; fatal: boolean; error?: any }> => {
      if (refreshInProgress) {
        logger.debug(`[AUTH] Refresh skipped (${origin}) - already in progress`);
        return { ok: true, fatal: false };
      }
      refreshInProgress = true;
      try {
        // 3 tentatives avec backoff (1s, 3s) - ne déconnecte que si fatal confirmé
        for (let attempt = 0; attempt < 3; attempt++) {
          const { error } = await supabase.auth.refreshSession();
          if (!error) {
            lastSuccessfulRefresh = Date.now();
            return { ok: true, fatal: false };
          }
          if (isFatalRefreshError(error.message)) {
            logger.warn(`[AUTH] Refresh fatal (${origin}, attempt ${attempt + 1}):`, error.message);
            return { ok: false, fatal: true, error };
          }
          logger.warn(`[AUTH] Refresh transient (${origin}, attempt ${attempt + 1}):`, error.message);
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, attempt === 0 ? 1000 : 3000));
          }
        }
        // 3 échecs non-fatals = problème réseau, on garde la session
        logger.warn(`[AUTH] Refresh failed 3x non-fatal (${origin}) - keeping session`);
        return { ok: false, fatal: false };
      } finally {
        refreshInProgress = false;
      }
    };

    // 3. Refresh proactif toutes les 50 minutes (avant expiration de 1h)
    const refreshInterval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        logger.debug('[AUTH] Refresh proactif de la session');
        const result = await safeRefreshSession('proactive');

        // ✅ Ne déconnecter QUE si erreur fatale confirmée (token révoqué)
        if (result.fatal) {
          // « Already used » app vivante = souvent la rotation native (le
          // Keychain détient le seul token vivant) : récupérer avant de
          // déconnecter (revue 03/08 — le cas resume de suspension battait
          // la récupération du SIGNED_OUT).
          if (await tryNativeSessionRecovery()) {
            lastSuccessfulRefresh = Date.now();
            return;
          }
          forcedSignOutRef.current = true;
          await supabase.auth.signOut();
          window.location.href = '/login?reason=session_expired';
          return;
        }
        if (!result.ok) return; // erreur transitoire réseau - on attend
        
        // ✅ Vérifier le status en base après chaque refresh
        try {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('status')
            .eq('id', session.user.id)
            .maybeSingle();
          
          if (profile?.status === 'rejected') {
            logger.warn('[AUTH] Proactive check: account suspended, forcing sign out');
            forcedSignOutRef.current = true;
            await supabase.auth.signOut();
            window.location.href = '/login?reason=account_disabled';
          }
        } catch (e) {
          logger.warn('[AUTH] Proactive status check failed (non-blocking):', e);
        }
      }
    }, 50 * 60 * 1000);

    // 4. Gérer le retour de l'app en arrière-plan (debounce 5min, mutex partagé)
    let lastVisibilityCheck = 0;
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // ✅ Debounce étendu à 5 min + skip si refresh récent OK
        if (now - lastVisibilityCheck < 5 * 60 * 1000) {
          logger.debug('[AUTH] Skipping visibility refresh (debounce 5min)');
          return;
        }
        if (now - lastSuccessfulRefresh < 5 * 60 * 1000) {
          logger.debug('[AUTH] Skipping visibility refresh (recent successful refresh)');
          return;
        }
        lastVisibilityCheck = now;

        logger.debug('[AUTH] App revenue au premier plan, vérification session');
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const result = await safeRefreshSession('visibility');

          // ✅ Ne déconnecter QUE si erreur fatale (token révoqué confirmé)
          if (result.fatal) {
            // Cas resume typique : token roté par le natif pendant la
            // suspension → le Keychain a le seul token vivant.
            if (await tryNativeSessionRecovery()) {
              lastSuccessfulRefresh = Date.now();
              return;
            }
            forcedSignOutRef.current = true;
            await supabase.auth.signOut();
            window.location.href = '/login?reason=session_expired';
            return;
          }
          if (!result.ok) return; // erreur réseau transitoire
          
          // ✅ Vérifier le status en base après le refresh
          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('status')
              .eq('id', session.user.id)
              .maybeSingle();
            
            if (profile?.status === 'rejected') {
              logger.warn('[AUTH] Visibility check: account suspended, forcing sign out');
              forcedSignOutRef.current = true;
              await supabase.auth.signOut();
              window.location.href = '/login?reason=account_disabled';
            }
          } catch (e) {
            logger.warn('[AUTH] Visibility status check failed (non-blocking):', e);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const checkAuth = async (): Promise<boolean> => {
    try {
      const currentUser = await supabaseAuthService.getCurrentUser();
      setUser(currentUser);
      return !!currentUser;
    } catch (error) {
      logger.error('Error checking authentication:', error);
      return false;
    }
  };

  const login = async (email: string, password: string, captchaToken?: string | null): Promise<void> => {
    logger.debug('[AUTH LOGIC] Login attempt for:', { email });
    setIsLoading(true);
    try {
      await supabaseAuthService.login(email, password, captchaToken);
      logger.debug('[AUTH LOGIC] Login successful, waiting for onAuthStateChange...');

      // 🔐 Device fingerprinting + OneSignal registration - Exécuté en arrière-plan
      setTimeout(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            // Device fingerprinting
            logger.debug('[AUTH LOGIC] Checking device fingerprint...');
            const deviceResult = await checkAndRegisterDevice(session.user.id);

            if (deviceResult.isNewDevice && deviceResult.deviceId) {
              logger.debug('[AUTH LOGIC] New device detected, sending notification...');

              const { data: profile } = await supabase
                .from('user_profiles')
                .select('organization_id')
                .eq('id', session.user.id)
                .single();

              await notifyNewDeviceLogin(
                session.user.id,
                deviceResult.deviceId,
                deviceResult.deviceInfo,
                profile?.organization_id || undefined
              );
            }

            // Note: OneSignal registration is handled in onAuthStateChange (single entry point)
          }
        } catch (deviceError) {
          logger.warn('[AUTH LOGIC] Device/OneSignal check failed (non-blocking):', deviceError);
        }
      }, 100);

    } catch (error) {
      logger.error('[AUTH LOGIC] Login failed:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const register = async (userData: Partial<User> & { password: string; captchaToken?: string | null }): Promise<void> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email!,
        password: userData.password,
        options: withCaptcha({
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: userData.name,
            nickname: userData.nickname,
            user_type: userData.userType || 'client',
            // ✅ FIX: Transmettre l'organization_id pour handle_new_user()
            organizationId: userData.organizationId || null,
          }
        }, userData.captchaToken)
      });

      if (error) throw error;
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    // ✅ FIX: Éviter double déclenchement
    if (logoutInProgressRef.current || isLoggingOut) {
      logger.debug('[AUTH] Logout already in progress, ignoring');
      return;
    }
    
    // ✅ FIX LOGOUT CRASH: Acquérir le lock de transition AVANT tout
    if (!appTransitionState.setState('logging_out')) {
      const currentState = appTransitionState.getState();
      logger.warn('[AUTH] Could not acquire transition lock for logout, current:', currentState);
      
      // ✅ Si le verrou est bloqué en boot_recovery, forcer le reset
      // Cela évite que l'utilisateur soit coincé indéfiniment
      if (currentState === 'boot_recovery') {
        logger.warn('[AUTH] Boot recovery stuck, forcing reset...');
        appTransitionState.forceState('logging_out');
        // Continuer le logout normalement
      } else {
        toast.info('Veuillez patienter...', {
          description: 'Une opération est en cours, réessayez dans un instant.',
          duration: 2000,
        });
        return;
      }
    }
    
    logger.debug('[AUTH] Logout initiated - CONTROLLED mode (transition locked)');
    
    // 🚨 ÉTAPE 1: Marquer logout en cours (ref + state + crash marker)
    logoutInProgressRef.current = true;
    setIsLoggingOut(true);
    setIsAuthStable(false); // ✅ Marquer auth comme instable pendant le logout
    crashMarkerService.markLogoutStart();
    
    // 🚨 ÉTAPE 2: Capturer les infos AVANT de tout nettoyer
    const currentUserId = user?.id;
    const isNative = medianNativeBridge.isMedianNative();
    const hasBiometric = medianBiometricAuth.isAvailable();
    
    try {
      // 🚨 PHASE 3: CAPTURE OneSignal info BEFORE logout for multi-account tracking
      if (isNative) {
        try {
          const info = await medianNativeBridge.getOneSignalInfo();
          if (info?.userId) {
            localStorage.setItem('last_onesignal_user_id', info.userId);
            if (info.subscriptionId) {
              localStorage.setItem('last_onesignal_subscription_id', info.subscriptionId);
            }
            logger.debug('[AUTH] 📝 OneSignal info captured before logout:', info.userId.slice(0, 8));
          }
        } catch (e) {
          logger.debug('[AUTH] Could not capture OneSignal info before logout (non-blocking)');
        }
      }
      
      // 🚨 ÉTAPE 3: CRITIQUE - Désactiver le push token AVANT signOut (JWT encore valide)
      if (currentUserId && isNative) {
        try {
          await Promise.race([
            pushTokenService.deactivatePushToken(currentUserId),
            new Promise((_, reject) => setTimeout(() => reject(new Error('deactivatePushToken timeout')), 3000))
          ]);
          logger.debug('[AUTH] ✅ Push token deactivated (JWT still valid)');
        } catch (e) {
          logger.warn('[AUTH] Push token deactivation failed (non-blocking):', e);
        }
      }

      // 🚨 ÉTAPE 4: OneSignal logout
      if (isNative) {
        logger.debug('[AUTH] Calling OneSignal logout()...');
        logoutOneSignal();
        logger.debug('[AUTH] ✅ OneSignal logout completed');
      }
      
      // 🚨 ÉTAPE 5: Supabase signOut (avec timeout)
      logger.debug('[AUTH] Awaiting Supabase signOut...');
      await Promise.race([
        supabaseAuthService.logout(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('SignOut timeout')), 3000))
      ]);
      logger.debug('[AUTH] Supabase signOut completed');
    } catch (signOutError) {
      logger.warn('[AUTH] Supabase signOut failed/timeout:', signOutError);
      // Continuer le cleanup même si signOut échoue. Mais purger la clé de
      // session locale : un signOut en échec réseau la laissait en place, et
      // la détection légère des pages publiques (MainNavbar, gabarit légal,
      // clés sb-*-auth-token) croyait l'utilisateur toujours connecté.
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {
        // Stockage indisponible : rien à purger.
      }
    }
    
    // 🚨 ÉTAPE 4: CRITIQUE - Clear native storage EXPLICITEMENT
    if (isNative) {
      try {
        await persistentAuthStorage.clearSession();
        logger.debug('[AUTH] Native storage cleared explicitly');
      } catch (e) {
        logger.warn('[AUTH] Failed to clear native storage:', e);
      }
    }
    
    // 🚨 ÉTAPE 5: Clear React Query cache
    try {
      queryClient.clear();
      logger.debug('[AUTH] React Query cache cleared');
    } catch (e) {
      logger.warn('[AUTH] queryClient.clear failed:', e);
    }

    // 🚨 ÉTAPE 5bis: P0 #8 audit pré-pilote — Clear caches localStorage privés
    // (matching covoit contient coords approximatives ~110m de collègues, ne
    //  doit pas survivre au signOut sur device partagé)
    try {
      const cleared = clearCarpoolMatchingCache();
      if (cleared > 0) {
        logger.debug(`[AUTH] Carpool matching cache cleared: ${cleared} entries`);
      }
    } catch (e) {
      logger.warn('[AUTH] clearCarpoolMatchingCache failed:', e);
    }
    
    // 🚨 ÉTAPE 6: Nettoyage natif (non-bloquant)
      // 🚨 ÉTAPE 8: Nettoyage localStorage push (token déjà désactivé à l'étape 3)
      if (currentUserId && isNative) {
        try {
          localStorage.removeItem('last_onesignal_user_id');
          localStorage.removeItem('last_onesignal_subscription_id');
          localStorage.removeItem(`push_verify_session_${currentUserId}`);
          localStorage.removeItem(`push_external_id_last_verified_${currentUserId}`);
          logger.debug('[AUTH] ✅ Push localStorage cleaned');
        } catch { /* ignore */ }
      }
    
    // 🔐 NE PLUS supprimer le secret biométrique au logout normal.
    // Le secret reste chiffré côté natif (Keychain/Keystore), protégé par biométrie.
    // Il n'est supprimé que :
    //  - sur action explicite "Désactiver" depuis Réglages → Sécurité
    //  - si refreshSession() échoue avec token révoqué (cf. BiometricLogin.tsx)
    // → l'utilisateur peut se reconnecter en Face ID après une session expirée.
    void hasBiometric; // conservé pour usage futur (logout "complet")
    
    // 🚨 ÉTAPE 7: Mettre user à null
    setUser(null);
    
    // 🚨 ÉTAPE 8: Marquer clean shutdown AVANT la navigation
    crashMarkerService.markCleanShutdown();
    
    logger.debug('[AUTH] Logout complete, navigating to /login...');
    
    // ✅ FIX LOGOUT CRASH: Utiliser une navigation plus douce avec fallback
    // On laisse un petit délai pour que React mette à jour l'UI
    requestAnimationFrame(() => {
      // Reset transition state juste avant la navigation
      appTransitionState.reset();
      
      // Navigation vers /login - utiliser replace pour éviter le back
      window.location.replace('/login');
    });
  };


  const updateNickname = async (nickname: string): Promise<void> => {
    if (!user) return;

    // Démarrer l'indicateur de sauvegarde
    const operationId = useSaveIndicatorStore.getState().startSave('generic');

    try {
      const updatedUser = await supabaseAuthService.updateNickname(user.id, nickname);
      setUser(updatedUser);

      // ✅ FIX: Émettre événement pour invalider le cache leaderboard
      const { eventBus } = await import('@/lib/eventBus');
      eventBus.emit('profile:updated', { userId: user.id, field: 'nickname' });

      // ✅ FIX: Invalider ET réinitialiser complètement le cache employees + leaderboard
      // resetQueries force un refetch complet, pas juste une invalidation
      queryClient.resetQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['enriched-employees'] });

      useSaveIndicatorStore.getState().endSave(operationId);
      logger.debug('[AUTH LOGIC] Nickname updated and ALL leaderboard caches invalidated');
    } catch (error) {
      useSaveIndicatorStore.getState().endSave(operationId, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  };

  const updateProfile = async (updates: { name?: string; avatar?: string | null; department?: string }): Promise<void> => {
    if (!user) return;

    // Démarrer l'indicateur de sauvegarde
    const operationId = useSaveIndicatorStore.getState().startSave('generic');

    // MISE À JOUR OPTIMISTE : UI mise à jour instantanément
    // `avatar: null` (retrait de la photo) est volontaire : on le normalise en
    // `undefined` cote modele UI, qui ne connait que « pas de photo ».
    const optimisticUser = { ...user, ...updates, avatar: updates.avatar ?? undefined };
    setUser(optimisticUser);

    try {
      // Appel API en arrière-plan
      const updatedUser = await supabaseAuthService.updateProfile(user.id, updates);
      setUser(updatedUser); // Confirme avec les vraies données

      // Invalidation en arrière-plan (non bloquante)
      queryClient.invalidateQueries({ queryKey: ['employees'] });

      // Notifier le succès
      useSaveIndicatorStore.getState().endSave(operationId);
      logger.debug('[AUTH LOGIC] Profil mis à jour avec succès (optimiste)');
    } catch (error) {
      // Notifier l'erreur et rollback
      useSaveIndicatorStore.getState().endSave(operationId, error instanceof Error ? error.message : 'Unknown error');
      setUser(user);
      logger.error('[AUTH LOGIC] Erreur mise à jour profil, rollback effectué');
      throw error;
    }
  };

  const createUserFromRegistrationRequest = async (requestData: any, managerId: string, departmentId?: string, teamId?: string): Promise<User> => {
    throw new Error('Not implemented yet for Supabase');
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingOut,
    isAuthStable,
    login,
    register,
    logout,
    checkAuth,
    updateNickname,
    updateProfile,
    createUserFromRegistrationRequest,
  };
};
